import { lazy } from 'react';
import { Navigate, type RouteObject } from 'react-router-dom';
import AuthRoute from './AuthRoute';
import { USER_ROLE } from '@/shared/constants';

import MainLayout from '@/shared/components/layout/MainLayout';
import PageWrapper from './PageWrapper';

import { PATH } from '@/shared/constants';

const InstructorHomePage = lazy(() => import('@/pages/instructor/home/Page'));
const InstructorAttendancePage = lazy(() => import('@/pages/instructor/attendance/Page'));
const InstructorLessonPage = lazy(() => import('@/pages/instructor/lesson/Page'));

const InstructorLessonDetailPage = lazy(() => import('@/pages/instructor/lesson/detail/[lessonId]/Page'));
const InstructorLessonBoardLayout = lazy(() => import('@/pages/instructor/lesson/board/[lessonId]/Layout'));

const InstructorLessonBoardClassNoticePage = lazy(() => import('@/pages/instructor/lesson/board/[lessonId]/class-notice/Page'));
const InstructorLessonBoardNoticeDetailPage = lazy(() => import('@/pages/instructor/lesson/board/[lessonId]/class-notice/[noticeId]/Page'));

const InstructorLessonBoardProgressVodPage = lazy(() => import('@/pages/instructor/lesson/board/[lessonId]/progress-vod/Page'));
const InstructorLessonBoardProgressVodDetailPage = lazy(() => import('@/pages/instructor/lesson/board/[lessonId]/progress-vod/[vodId]/Page'));

const InstructorLessonBoardQuizAssignmentPage = lazy(() => import('@/pages/instructor/lesson/board/[lessonId]/quiz-assignment/Page'));
const InstructorLessonBoardQuizAssignmentDetailPage = lazy(() => import('@/pages/instructor/lesson/board/[lessonId]/quiz-assignment/[quizAssignmentId]/Page'));

const InstructorLessonBoardWritePage = lazy(() => import('@/pages/instructor/lesson/board/[lessonId]/write/Page'));
const InstructorLessonBoardEditPage = lazy(() => import('@/pages/instructor/lesson/board/[lessonId]/edit/Page'));

const InstructorStudentPage = lazy(() => import('@/pages/instructor/student/Page'));
const InstructorCounselingPage = lazy(() => import('@/pages/instructor/counseling/Page'));
const InstructorCounselingAddPage = lazy(() => import('@/pages/instructor/counseling/add/Page'));
const InstructorCounselingDetailPage = lazy(() => import('@/pages/instructor/counseling/detail/[consultationId]/Page'));
const InstructorNoticePage = lazy(() => import('@/pages/instructor/notice/Page'));
const InstructorNoticeDetailPage = lazy(() => import('@/pages/instructor/notice/detail/[noticeId]/Page'));

export const instructorRoutes: RouteObject = {
  path: '/instructor',
  element: <AuthRoute allowedRoles={[USER_ROLE.INSTRUCTOR]} />,
  children: [
    {
      element: <MainLayout />,
      children: [
        { index: true, element: <Navigate to='home' replace /> },
        {
          path: 'home',
          element: (
            <PageWrapper>
              <InstructorHomePage />
            </PageWrapper>
          ),
        },
        {
          path: 'attendance',
          element: (
            <PageWrapper>
              <InstructorAttendancePage />
            </PageWrapper>
          ),
        },
        {
          path: 'lesson',
          children: [
            {
              index: true,
              element: (
                <PageWrapper>
                  <InstructorLessonPage />
                </PageWrapper>
              ),
            },
            {
              path: `${PATH.INSTRUCTOR.LESSON.DETAIL.INDEX}:lessonId`,
              element: (
                <PageWrapper>
                  <InstructorLessonDetailPage />
                </PageWrapper>
              ),
            },
            {
              path: PATH.INSTRUCTOR.LESSON.BOARD.INDEX,
              children: [
                {
                  path: ':lessonId',
                  element: (
                    <PageWrapper>
                      <InstructorLessonBoardLayout />
                    </PageWrapper>
                  ),
                  children: [
                    { index: true, element: <Navigate to='class-notice' replace /> },
                    {
                      path: 'class-notice',
                      element: (
                        <PageWrapper>
                          <InstructorLessonBoardClassNoticePage />
                        </PageWrapper>
                      ),
                    },
                    {
                      path: 'class-notice/:noticeId',
                      element: (
                        <PageWrapper>
                          <InstructorLessonBoardNoticeDetailPage />
                        </PageWrapper>
                      ),
                    },
                    {
                      path: 'progress-vod',
                      element: (
                        <PageWrapper>
                          <InstructorLessonBoardProgressVodPage />
                        </PageWrapper>
                      ),
                    },
                    {
                      path: 'progress-vod/:vodId',
                      element: (
                        <PageWrapper>
                          <InstructorLessonBoardProgressVodDetailPage />
                        </PageWrapper>
                      ),
                    },
                    {
                      path: 'quiz-assignment',
                      element: (
                        <PageWrapper>
                          <InstructorLessonBoardQuizAssignmentPage />
                        </PageWrapper>
                      ),
                    },
                    {
                      path: `${PATH.INSTRUCTOR.LESSON.BOARD.QUIZ_ASSIGNMENT}/:quizAssignmentId`,
                      element: (
                        <PageWrapper>
                          <InstructorLessonBoardQuizAssignmentDetailPage />
                        </PageWrapper>
                      ),
                    },
                    {
                      path: 'write',
                      element: (
                        <PageWrapper>
                          <InstructorLessonBoardWritePage />
                        </PageWrapper>
                      ),
                    },
                    {
                      path: 'edit/:postId',
                      element: (
                        <PageWrapper>
                          <InstructorLessonBoardEditPage />
                        </PageWrapper>
                      ),
                    },
                  ],
                },
              ],
            },
          ],
        },

        {
          path: 'student',
          element: (
            <PageWrapper>
              <InstructorStudentPage />
            </PageWrapper>
          ),
        },
        {
          path: 'counseling',
          children: [
            {
              index: true,
              element: (
                <PageWrapper>
                  <InstructorCounselingPage />
                </PageWrapper>
              ),
            },
            {
              path: 'add',
              element: (
                <PageWrapper>
                  <InstructorCounselingAddPage />
                </PageWrapper>
              ),
            },
            {
              path: 'detail/:consultationId',
              element: (
                <PageWrapper>
                  <InstructorCounselingDetailPage />
                </PageWrapper>
              ),
            },
          ],
        },
        {
          path: 'notice',
          children: [
            {
              index: true,
              element: (
                <PageWrapper>
                  <InstructorNoticePage />
                </PageWrapper>
              ),
            },
            {
              path: 'detail/:noticeId',
              element: (
                <PageWrapper>
                  <InstructorNoticeDetailPage />
                </PageWrapper>
              ),
            },
          ],
        },
      ],
    },
  ],
};
